import { useState } from 'react';
import { Popover } from 'radix-ui';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { Icon } from './uploadUi';
import {
  MONTH_NAMES,
  formatFriday,
  formatFridayShort,
  hijriApprox,
  monthGrid,
  parseIsoDate,
} from './fridays';

/**
 * Which Friday this khutbah is for.
 *
 * A month calendar, because picking a khutbah two months out should not mean
 * pressing an arrow nine times — but **only Fridays are selectable**. Every
 * other day is rendered as a disabled cell rather than hidden: an editor
 * needs to see the shape of the month to find the Friday they mean, and a
 * grid with five cells and thirty holes is unreadable.
 *
 * Disabled, not merely ignored. A khutbah filed on a Tuesday never appears
 * where a reader looks for it, and nothing about it looks wrong afterwards —
 * so the wrong answer is made unreachable rather than corrected later.
 *
 * Drawn to the design system's `Calendar`: Monday-first, 44px cells, the
 * Friday column carried in the foreground weight, today outlined, the
 * selected day filled.
 */

/** Monday-first, matching the design system's day-of-week row. */
const DAYS_OF_WEEK = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/** A decade either side covers every khutbah anyone will file by hand. */
function yearRange(around: number): number[] {
  return Array.from({ length: 11 }, (_, i) => around - 5 + i);
}

const CONTROL = cn(
  'h-8 rounded-md border border-border bg-background px-2 text-[13px]',
  'outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
);

function Grid({
  year,
  month,
  selected,
  onPick,
}: {
  year: number;
  month: number;
  selected: string;
  onPick: (iso: string) => void;
}) {
  const todayIso = (() => {
    const now = new Date();
    return `${now.getFullYear()}-${`${now.getMonth() + 1}`.padStart(2, '0')}-${`${now.getDate()}`.padStart(2, '0')}`;
  })();

  return (
    <div role="grid" aria-label={`${MONTH_NAMES[month]} ${year}`}>
      <div role="row" className="grid grid-cols-7 gap-0.5">
        {DAYS_OF_WEEK.map((name, index) => (
          <div
            key={name}
            role="columnheader"
            aria-label={name}
            className={cn(
              'py-1 text-center text-[11px] font-semibold leading-4 tracking-[0.04em]',
              // The Friday column is carried in the foreground weight, as the
              // design system specifies — it is the one that matters here.
              index === 4 ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {name}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {monthGrid(year, month).map((cell) => {
          const isSelected = cell.iso === selected;
          const isToday = cell.iso === todayIso;

          if (!cell.friday) {
            return (
              <div
                key={cell.iso}
                role="gridcell"
                aria-disabled
                // Not a <button disabled>: there is no action here at all, and
                // a disabled button in a grid is still announced as a control
                // the reader might have been able to use.
                className={cn(
                  'flex min-h-11 items-center justify-center rounded-sm text-[13px]',
                  'text-muted-foreground/40',
                  isToday && 'outline outline-1 outline-border',
                )}
              >
                {cell.day}
              </div>
            );
          }

          return (
            <button
              key={cell.iso}
              type="button"
              role="gridcell"
              aria-selected={isSelected}
              aria-label={formatFriday(cell.iso)}
              onClick={() => onPick(cell.iso)}
              className={cn(
                'flex min-h-11 items-center justify-center rounded-sm border border-transparent',
                'text-[13px] font-semibold outline-none',
                'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
                isSelected
                  ? 'bg-primary text-primary-foreground'
                  : cn(
                      cell.outside ? 'text-muted-foreground' : 'text-foreground',
                      'hover:bg-accent',
                    ),
                isToday && !isSelected && 'border-primary',
              )}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function FridayCalendar({
  value,
  onChange,
  disabled = false,
}: {
  value: string;
  onChange: (iso: string) => void;
  disabled?: boolean;
}) {
  const selected = parseIsoDate(value) ?? new Date();
  const [open, setOpen] = useState(false);
  // Which month the grid is showing — not the same thing as the chosen
  // Friday, because browsing away from it must not change the answer.
  const [view, setView] = useState({
    year: selected.getFullYear(),
    month: selected.getMonth(),
  });
  const hijri = hijriApprox(value);

  function step(months: number) {
    const date = new Date(view.year, view.month + months, 1);
    setView({ year: date.getFullYear(), month: date.getMonth() });
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[13px] font-medium" id="friday-label">
        Friday this khutbah is for
      </span>

      <Popover.Root
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          // Reopening always lands on the chosen Friday's month, not wherever
          // the last browse ended up.
          if (next) {
            const current = parseIsoDate(value) ?? new Date();
            setView({ year: current.getFullYear(), month: current.getMonth() });
          }
        }}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-labelledby="friday-label"
            className={cn(
              'flex h-11 w-full items-center gap-2 rounded-lg border border-border bg-background px-3',
              'text-start outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
              'disabled:opacity-60',
            )}
          >
            <Icon name="calendar" size={16} className="text-muted-foreground" />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[13px] font-semibold leading-tight">
                {formatFridayShort(value)}
              </span>
              {hijri ? (
                <span className="truncate text-[11px] leading-tight text-muted-foreground">
                  ≈ {hijri}
                </span>
              ) : null}
            </span>
            <Icon name="chevron" size={16} className="text-muted-foreground" />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={6}
            className="z-50 w-[336px] rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-2"
          >
            <div className="mb-2 flex items-center gap-1.5">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => step(-1)}
                aria-label="Previous month"
              >
                <Icon name="chevron" size={16} className="rotate-90 rtl:-rotate-90" />
              </Button>

              <select
                aria-label="Month"
                value={view.month}
                onChange={(event) =>
                  setView({ ...view, month: Number(event.target.value) })
                }
                className={cn(CONTROL, 'flex-1')}
              >
                {MONTH_NAMES.map((name, index) => (
                  <option key={name} value={index}>
                    {name}
                  </option>
                ))}
              </select>

              <select
                aria-label="Year"
                value={view.year}
                onChange={(event) =>
                  setView({ ...view, year: Number(event.target.value) })
                }
                className={CONTROL}
              >
                {yearRange(new Date().getFullYear()).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => step(1)}
                aria-label="Next month"
              >
                <Icon name="chevron" size={16} className="-rotate-90 rtl:rotate-90" />
              </Button>
            </div>

            <Grid
              year={view.year}
              month={view.month}
              selected={value}
              onPick={(iso) => {
                onChange(iso);
                setOpen(false);
              }}
            />

            <p className="mt-2 text-[11px] text-muted-foreground">
              Only Fridays can be chosen — a khutbah belongs to a Friday.
            </p>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
